import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import type { InstrumentDefinition } from "../domain/instrument";
import type {
  AnnotationTarget,
  AttachmentReference,
  CustomSetting,
  PatchAnnotation,
  PatchDocument,
  PerformanceState,
} from "../domain/patch";
import { loadAttachmentBinary } from "../persistence/database";
import { compiledInstrumentDefinitionForDocument } from "../workspace/workspace-ports";
import { useEditorStore } from "./store";

const PHOTO_LIMIT = 25 * 1024 * 1024;
const AUDIO_LIMIT = 50 * 1024 * 1024;

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds?: number) {
  if (!seconds || !Number.isFinite(seconds)) return null;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${remainder}`;
}

function useAttachmentUrls(attachments: AttachmentReference[]) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const key = attachments.map((attachment) => `${attachment.id}:${attachment.size}`).join("|");

  useEffect(() => {
    let current = true;
    const objectUrls: string[] = [];
    void Promise.all(
      attachments.map(async (attachment) => {
        const blob = await loadAttachmentBinary(attachment.id);
        if (!blob) return null;
        const url = URL.createObjectURL(blob);
        objectUrls.push(url);
        return [attachment.id, url] as const;
      }),
    ).then((entries) => {
      if (current) setUrls(Object.fromEntries(entries.filter((entry) => entry !== null)));
    });
    return () => {
      current = false;
      for (const url of objectUrls) URL.revokeObjectURL(url);
    };
  }, [attachments, key]);

  return urls;
}

export function NotesInspector({ document }: { document: PatchDocument }) {
  const updatePatchNotes = useEditorStore((state) => state.updatePatchNotes);
  const [body, setBody] = useState(document.notes.body);
  const words = body.trim() ? body.trim().split(/\s+/).length : 0;
  const changed = body !== document.notes.body;

  return (
    <>
      <form
        className="documentation-editor notes-editor"
        onSubmit={(event) => {
          event.preventDefault();
          updatePatchNotes(body);
        }}
      >
        <div className="documentation-heading">
          <div>
            <span className="object-type">Patch notebook</span>
            <h3>Working notes</h3>
          </div>
          <span>{words} words</span>
        </div>
        <p>
          Record setup order, tuning, external equipment, performance cues, or anything that will
          help reconstruct the patch.
        </p>
        <label>
          <span>Notes</span>
          <textarea
            aria-label="Patch notes"
            onChange={(event) => setBody(event.target.value)}
            placeholder="Start with the setup sequence or the detail you are most likely to forget…"
            rows={14}
            value={body}
          />
        </label>
        <div className="documentation-actions">
          {changed && <small>Unsaved note changes</small>}
          <button disabled={!changed} type="submit">
            <Icon name="save" size={15} /> Save notes
          </button>
        </div>
      </form>
      <LinkedAnnotationsInspector document={document} />
      <CustomSettingsInspector document={document} />
      <PerformanceStatesInspector document={document} />
    </>
  );
}

function annotationTargetKey(target: AnnotationTarget) {
  if (target.kind === "patch") return "patch";
  if (target.kind === "section") return `section:${target.sectionId}`;
  if (target.kind === "control") return `control:${target.controlId}`;
  if (target.kind === "port") return `port:${target.portId}`;
  return `cable:${target.cableId}`;
}

function parseAnnotationTarget(key: string): AnnotationTarget {
  const [kind, id] = key.split(":", 2);
  if (kind === "section" && id) return { kind, sectionId: id };
  if (kind === "control" && id) return { kind, controlId: id };
  if (kind === "port" && id) return { kind, portId: id };
  if (kind === "cable" && id) return { kind, cableId: id };
  return { kind: "patch" };
}

function annotationTargetLabel(
  target: AnnotationTarget,
  document: PatchDocument,
  definition: InstrumentDefinition,
) {
  if (target.kind === "patch") return "Whole patch";
  if (target.kind === "section") {
    return (
      definition.sections.find((section) => section.id === target.sectionId)?.label ??
      "Unavailable section"
    );
  }
  if (target.kind === "control") {
    return (
      definition.controls.find((control) => control.id === target.controlId)?.label ??
      "Unavailable control"
    );
  }
  if (target.kind === "port") {
    return definition.ports.find((port) => port.id === target.portId)?.label ?? "Unavailable port";
  }
  const cable = document.workspace.cableAssemblies.find((item) => item.id === target.cableId);
  return cable
    ? `Cable ${cable.number}${cable.label ? ` — ${cable.label}` : ""}`
    : "Unavailable cable";
}

function defaultAnnotationTarget(
  selectedId: string | null,
  document: PatchDocument,
  definition: InstrumentDefinition,
): AnnotationTarget {
  if (definition.controls.some((control) => control.id === selectedId)) {
    return { kind: "control", controlId: selectedId! };
  }
  if (definition.ports.some((port) => port.id === selectedId)) {
    return { kind: "port", portId: selectedId! };
  }
  if (document.workspace.cableAssemblies.some((cable) => cable.id === selectedId)) {
    return { kind: "cable", cableId: selectedId! };
  }
  return { kind: "patch" };
}

function LinkedAnnotationEditor({
  annotation,
  definition,
  document,
  onReveal,
}: {
  annotation: PatchAnnotation;
  definition: InstrumentDefinition;
  document: PatchDocument;
  onReveal: (target: AnnotationTarget) => void;
}) {
  const removeAnnotation = useEditorStore((state) => state.removeAnnotation);
  const updateAnnotation = useEditorStore((state) => state.updateAnnotation);
  const [text, setText] = useState(annotation.text);
  const changed = text.trim() !== annotation.text;

  return (
    <article className="linked-annotation-row">
      <div>
        <span>{annotation.target.kind}</span>
        <strong>{annotationTargetLabel(annotation.target, document, definition)}</strong>
      </div>
      <label>
        <span>Note</span>
        <textarea onChange={(event) => setText(event.target.value)} rows={3} value={text} />
      </label>
      <div className="structured-row-actions linked-annotation-actions">
        <button onClick={() => onReveal(annotation.target)} type="button">
          Show target
        </button>
        <button
          aria-label={`Remove linked note for ${annotationTargetLabel(annotation.target, document, definition)}`}
          className="is-danger"
          onClick={() => removeAnnotation(annotation.id)}
          type="button"
        >
          Remove
        </button>
        <button
          disabled={!changed || !text.trim()}
          onClick={() => updateAnnotation(annotation.id, text)}
          type="button"
        >
          Save
        </button>
      </div>
    </article>
  );
}

function LinkedAnnotationsInspector({ document }: { document: PatchDocument }) {
  const addAnnotation = useEditorStore((state) => state.addAnnotation);
  const focusSection = useEditorStore((state) => state.focusSection);
  const select = useEditorStore((state) => state.select);
  const selectedId = useEditorStore((state) => state.selectedId);
  const setInspectorTab = useEditorStore((state) => state.setInspectorTab);
  const setViewMode = useEditorStore((state) => state.setViewMode);
  const definition = useMemo(() => compiledInstrumentDefinitionForDocument(document), [document]);
  const [targetKey, setTargetKey] = useState(() =>
    annotationTargetKey(defaultAnnotationTarget(selectedId, document, definition)),
  );
  const [text, setText] = useState("");

  const revealTarget = (target: AnnotationTarget) => {
    if (target.kind === "patch") {
      select(null);
    } else if (target.kind === "section") {
      focusSection(target.sectionId);
      select(null);
    } else {
      const id =
        target.kind === "control"
          ? target.controlId
          : target.kind === "port"
            ? target.portId
            : target.cableId;
      select(id);
      const sectionId =
        target.kind === "control"
          ? definition.controls.find((control) => control.id === id)?.sectionId
          : target.kind === "port"
            ? definition.ports.find((port) => port.id === id)?.sectionId
            : undefined;
      if (sectionId) focusSection(sectionId);
    }
    setViewMode("panel");
    setInspectorTab("info");
  };

  return (
    <section
      aria-labelledby="linked-annotations-heading"
      className="structured-documentation linked-annotations-panel"
    >
      <div className="structured-heading">
        <div>
          <span className="object-type">Object notes</span>
          <h3 id="linked-annotations-heading">Linked annotations</h3>
        </div>
        <span>{document.workspace.annotations.length}</span>
      </div>
      <p>
        Attach a note to the complete patch or to a specific section, control, port, or patch cable.
      </p>
      {document.workspace.annotations.length > 0 && (
        <div className="structured-list">
          {document.workspace.annotations.map((annotation) => (
            <LinkedAnnotationEditor
              annotation={annotation}
              definition={definition}
              document={document}
              key={`${annotation.id}:${annotation.text}`}
              onReveal={revealTarget}
            />
          ))}
        </div>
      )}
      <form
        className="structured-add-form"
        onSubmit={(event) => {
          event.preventDefault();
          addAnnotation(parseAnnotationTarget(targetKey), text);
          setText("");
        }}
      >
        <strong>Add linked note</strong>
        <label>
          <span>Attach to</span>
          <select onChange={(event) => setTargetKey(event.target.value)} value={targetKey}>
            <option value="patch">Whole patch</option>
            <optgroup label="Sections">
              {definition.sections.map((section) => (
                <option key={section.id} value={`section:${section.id}`}>
                  {section.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="Controls">
              {definition.controls.map((control) => (
                <option key={control.id} value={`control:${control.id}`}>
                  {control.label}
                </option>
              ))}
            </optgroup>
            <optgroup label="Patch points">
              {definition.ports.map((port) => (
                <option key={port.id} value={`port:${port.id}`}>
                  {port.label}
                </option>
              ))}
            </optgroup>
            {document.workspace.cableAssemblies.length > 0 && (
              <optgroup label="Patch cables">
                {document.workspace.cableAssemblies.map((cable) => (
                  <option key={cable.id} value={`cable:${cable.id}`}>
                    Cable {cable.number}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </label>
        <label>
          <span>Note</span>
          <textarea
            onChange={(event) => setText(event.target.value)}
            placeholder="Describe the adjustment, reason, or performance cue…"
            rows={4}
            value={text}
          />
        </label>
        <button disabled={!text.trim()} type="submit">
          <Icon name="plus" size={14} /> Add linked note
        </button>
      </form>
    </section>
  );
}

const settingCategories: Array<{ value: CustomSetting["category"]; label: string }> = [
  { value: "midi", label: "MIDI" },
  { value: "clock", label: "Clock / sequence" },
  { value: "performance", label: "Performance" },
  { value: "external", label: "External equipment" },
  { value: "other", label: "Other" },
];

function CustomSettingEditor({ setting }: { setting: CustomSetting }) {
  const removeCustomSetting = useEditorStore((state) => state.removeCustomSetting);
  const updateCustomSetting = useEditorStore((state) => state.updateCustomSetting);
  const [label, setLabel] = useState(setting.label);
  const [value, setValue] = useState(setting.value);
  const [category, setCategory] = useState(setting.category);
  const changed =
    label !== setting.label || value !== setting.value || category !== setting.category;

  return (
    <article className="custom-setting-row">
      <div className="custom-setting-grid">
        <label>
          <span>Setting</span>
          <input onChange={(event) => setLabel(event.target.value)} value={label} />
        </label>
        <label>
          <span>Category</span>
          <select
            onChange={(event) => setCategory(event.target.value as CustomSetting["category"])}
            value={category}
          >
            {settingCategories.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label>
        <span>Documented value</span>
        <input onChange={(event) => setValue(event.target.value)} value={value} />
      </label>
      <div className="structured-row-actions">
        <button
          aria-label={`Remove custom setting ${setting.label}`}
          className="is-danger"
          onClick={() => removeCustomSetting(setting.id)}
          type="button"
        >
          Remove
        </button>
        <button
          disabled={!changed || !label.trim()}
          onClick={() => updateCustomSetting(setting.id, { category, label, value })}
          type="button"
        >
          Save
        </button>
      </div>
    </article>
  );
}

function CustomSettingsInspector({ document }: { document: PatchDocument }) {
  const addCustomSetting = useEditorStore((state) => state.addCustomSetting);
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [category, setCategory] = useState<CustomSetting["category"]>("performance");

  return (
    <section
      aria-labelledby="custom-settings-heading"
      className="structured-documentation custom-settings-panel"
    >
      <div className="structured-heading">
        <div>
          <span className="object-type">Structured recall</span>
          <h3 id="custom-settings-heading">Custom settings</h3>
        </div>
        <span>{document.customSettings.length}</span>
      </div>
      <p>
        Keep values that are not represented by a front-panel control, such as MIDI channel, clock
        source, startup sequence, or external equipment setup.
      </p>
      {document.customSettings.length > 0 && (
        <div className="structured-list">
          {document.customSettings.map((setting) => (
            <CustomSettingEditor
              key={`${setting.id}:${setting.label}:${setting.value}:${setting.category}`}
              setting={setting}
            />
          ))}
        </div>
      )}
      <form
        className="structured-add-form"
        onSubmit={(event) => {
          event.preventDefault();
          addCustomSetting(label, value, category);
          setLabel("");
          setValue("");
        }}
      >
        <strong>Add setting</strong>
        <label>
          <span>Name</span>
          <input
            onChange={(event) => setLabel(event.target.value)}
            placeholder="MIDI channel"
            value={label}
          />
        </label>
        <label>
          <span>Value</span>
          <input
            onChange={(event) => setValue(event.target.value)}
            placeholder="Channel 3"
            value={value}
          />
        </label>
        <label>
          <span>Category</span>
          <select
            onChange={(event) => setCategory(event.target.value as CustomSetting["category"])}
            value={category}
          >
            {settingCategories.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <button disabled={!label.trim()} type="submit">
          <Icon name="plus" size={14} /> Add setting
        </button>
      </form>
    </section>
  );
}

function differenceCount(current: Record<string, number>, captured: Record<string, number>) {
  return new Set([...Object.keys(current), ...Object.keys(captured)]).size
    ? [...new Set([...Object.keys(current), ...Object.keys(captured)])].filter(
        (key) => Math.abs((current[key] ?? 0) - (captured[key] ?? 0)) > 0.0001,
      ).length
    : 0;
}

function PerformanceStateEditor({
  currentControlValues,
  currentCables,
  currentSettings,
  first,
  last,
  performanceState,
}: {
  currentControlValues: Record<string, number>;
  currentCables: PatchDocument["workspace"]["cableAssemblies"];
  currentSettings: CustomSetting[];
  first: boolean;
  last: boolean;
  performanceState: PerformanceState;
}) {
  const applyPerformanceState = useEditorStore((state) => state.applyPerformanceState);
  const movePerformanceState = useEditorStore((state) => state.movePerformanceState);
  const removePerformanceState = useEditorStore((state) => state.removePerformanceState);
  const updatePerformanceState = useEditorStore((state) => state.updatePerformanceState);
  const [name, setName] = useState(performanceState.name);
  const [notes, setNotes] = useState(performanceState.notes);
  const controlChanges = differenceCount(currentControlValues, performanceState.controlValues);
  const cablesMatch =
    JSON.stringify(currentCables) === JSON.stringify(performanceState.cableAssemblies);
  const settingsMatch =
    JSON.stringify(currentSettings) === JSON.stringify(performanceState.customSettings);
  const matches = controlChanges === 0 && cablesMatch && settingsMatch;
  const changed = name !== performanceState.name || notes !== performanceState.notes;

  return (
    <article className={`performance-state-card${matches ? " is-current" : ""}`}>
      <div className="performance-state-status">
        <span>{matches ? "Matches current patch" : `${controlChanges} control differences`}</span>
        <time dateTime={performanceState.capturedAt}>
          {new Date(performanceState.capturedAt).toLocaleDateString()}
        </time>
      </div>
      <label>
        <span>State name</span>
        <input onChange={(event) => setName(event.target.value)} value={name} />
      </label>
      <label>
        <span>Transition notes</span>
        <textarea onChange={(event) => setNotes(event.target.value)} rows={3} value={notes} />
      </label>
      <div className="performance-state-facts">
        <span>{Object.keys(performanceState.controlValues).length} documented controls</span>
        <span>{performanceState.cableAssemblies.length} cables</span>
        <span>{performanceState.customSettings.length} custom settings</span>
        {!cablesMatch && <b>Cable layout differs</b>}
        {!settingsMatch && <b>Custom settings differ</b>}
      </div>
      <div className="state-order-actions">
        <button
          aria-label={`Move ${performanceState.name} earlier`}
          disabled={first}
          onClick={() => movePerformanceState(performanceState.id, -1)}
          type="button"
        >
          ↑
        </button>
        <button
          aria-label={`Move ${performanceState.name} later`}
          disabled={last}
          onClick={() => movePerformanceState(performanceState.id, 1)}
          type="button"
        >
          ↓
        </button>
        <button
          className="is-danger"
          onClick={() => removePerformanceState(performanceState.id)}
          type="button"
        >
          Delete
        </button>
        <button
          disabled={!changed || !name.trim()}
          onClick={() => updatePerformanceState(performanceState.id, { name, notes })}
          type="button"
        >
          Save
        </button>
        <button
          className="is-primary"
          disabled={matches}
          onClick={() => applyPerformanceState(performanceState.id)}
          type="button"
        >
          Apply state
        </button>
      </div>
    </article>
  );
}

function PerformanceStatesInspector({ document }: { document: PatchDocument }) {
  const capturePerformanceState = useEditorStore((state) => state.capturePerformanceState);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const currentNode = document.workspace.nodes.find((node) => node.kind === "instrument");
  const currentControlValues = currentNode?.controlValues ?? {};

  return (
    <section
      aria-labelledby="performance-states-heading"
      className="structured-documentation performance-states-panel"
    >
      <div className="structured-heading">
        <div>
          <span className="object-type">Live variations</span>
          <h3 id="performance-states-heading">Performance states</h3>
        </div>
        <span>{document.performanceStates.length}</span>
      </div>
      <p>
        Capture the current controls, cables, and custom settings as a named variation. Applying a
        state is undoable.
      </p>
      {document.performanceStates.length > 0 && (
        <div className="structured-list">
          {document.performanceStates.map((performanceState, index) => (
            <PerformanceStateEditor
              currentCables={document.workspace.cableAssemblies}
              currentControlValues={currentControlValues}
              currentSettings={document.customSettings}
              first={index === 0}
              key={`${performanceState.id}:${performanceState.name}:${performanceState.notes}`}
              last={index === document.performanceStates.length - 1}
              performanceState={performanceState}
            />
          ))}
        </div>
      )}
      <form
        className="structured-add-form"
        onSubmit={(event) => {
          event.preventDefault();
          capturePerformanceState(name, notes);
          setName("");
          setNotes("");
        }}
      >
        <strong>Capture current patch</strong>
        <label>
          <span>Name</span>
          <input
            onChange={(event) => setName(event.target.value)}
            placeholder="Intro"
            value={name}
          />
        </label>
        <label>
          <span>Transition notes</span>
          <textarea
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Bring the cutoff down before switching…"
            rows={3}
            value={notes}
          />
        </label>
        <button disabled={!name.trim()} type="submit">
          <Icon name="plus" size={14} /> Capture state
        </button>
      </form>
    </section>
  );
}

function attachmentDuration(file: File) {
  return new Promise<number | undefined>((resolve) => {
    const audio = document.createElement("audio");
    const url = URL.createObjectURL(file);
    const finish = (duration?: number) => {
      window.clearTimeout(timeout);
      URL.revokeObjectURL(url);
      resolve(duration);
    };
    const timeout = window.setTimeout(() => finish(), 2500);
    audio.preload = "metadata";
    audio.onloadedmetadata = () =>
      finish(Number.isFinite(audio.duration) ? audio.duration : undefined);
    audio.onerror = () => finish();
    audio.src = url;
  });
}

function PhotoViewer({
  attachments,
  onClose,
  onSelect,
  selectedId,
  urls,
}: {
  attachments: AttachmentReference[];
  onClose: () => void;
  onSelect: (attachmentId: string) => void;
  selectedId: string;
  urls: Record<string, string>;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const selectedIndex = Math.max(
    0,
    attachments.findIndex((attachment) => attachment.id === selectedId),
  );
  const attachment = attachments[selectedIndex]!;
  const selectOffset = (offset: number) => {
    const nextIndex = (selectedIndex + offset + attachments.length) % attachments.length;
    onSelect(attachments[nextIndex]!.id);
  };

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, []);

  const keydown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
    if (event.key === "ArrowLeft" && attachments.length > 1) {
      event.preventDefault();
      onSelect(attachments[(selectedIndex - 1 + attachments.length) % attachments.length]!.id);
    }
    if (event.key === "ArrowRight" && attachments.length > 1) {
      event.preventDefault();
      onSelect(attachments[(selectedIndex + 1) % attachments.length]!.id);
    }
    if (event.key === "Tab") {
      const viewer = closeRef.current?.closest(".media-viewer");
      const focusable = [...(viewer?.querySelectorAll<HTMLElement>("button:not(:disabled)") ?? [])];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (first && last && event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (first && last && !event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div
      className="media-viewer-backdrop"
      onKeyDown={keydown}
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section
        aria-label={`Photo viewer: ${attachment.title}`}
        aria-modal="true"
        className="media-viewer"
        role="dialog"
      >
        <header>
          <div>
            <span className="object-type">
              Photo {selectedIndex + 1} of {attachments.length}
            </span>
            <h2>{attachment.title}</h2>
          </div>
          <button aria-label="Close photo viewer" onClick={onClose} ref={closeRef} type="button">
            <Icon name="close" size={19} />
          </button>
        </header>
        <div className="media-viewer-stage">
          {attachments.length > 1 && (
            <button
              aria-label="Previous photo"
              className="media-viewer-previous"
              onClick={() => selectOffset(-1)}
              type="button"
            >
              ‹
            </button>
          )}
          <img
            alt={attachment.description || attachment.title}
            src={urls[attachment.id]}
            style={{ transform: `rotate(${attachment.rotation ?? 0}deg)` }}
          />
          {attachments.length > 1 && (
            <button
              aria-label="Next photo"
              className="media-viewer-next"
              onClick={() => selectOffset(1)}
              type="button"
            >
              ›
            </button>
          )}
        </div>
        <footer>
          <p>{attachment.description || "No caption added."}</p>
          <span>
            {attachment.filename} · {formatBytes(attachment.size)}
          </span>
        </footer>
      </section>
    </div>
  );
}

const fallbackPeaks = Array.from({ length: 80 }, (_, index) => {
  const envelope = Math.sin((index / 79) * Math.PI);
  return Math.max(0.08, envelope * (0.42 + ((index * 17) % 29) / 50));
});

function useAudioWaveform(attachmentId: string) {
  const [peaks, setPeaks] = useState(fallbackPeaks);
  const [decoded, setDecoded] = useState(false);

  useEffect(() => {
    let current = true;
    let context: AudioContext | undefined;
    void (async () => {
      try {
        const blob = await loadAttachmentBinary(attachmentId);
        if (!blob || !window.AudioContext) return;
        context = new AudioContext();
        const buffer = await context.decodeAudioData(await blob.arrayBuffer());
        const channel = buffer.getChannelData(0);
        const bucketSize = Math.max(1, Math.floor(channel.length / 80));
        const nextPeaks = Array.from({ length: 80 }, (_, index) => {
          const start = index * bucketSize;
          const end = Math.min(channel.length, start + bucketSize);
          let peak = 0;
          for (let sample = start; sample < end; sample += 1) {
            peak = Math.max(peak, Math.abs(channel[sample] ?? 0));
          }
          return peak;
        });
        const maximum = Math.max(...nextPeaks, 0.001);
        if (current) {
          setPeaks(nextPeaks.map((peak) => Math.max(0.025, peak / maximum)));
          setDecoded(true);
        }
      } catch {
        // Keep the deterministic placeholder when this browser cannot decode the file.
      } finally {
        if (context && context.state !== "closed") void context.close().catch(() => undefined);
      }
    })();
    return () => {
      current = false;
      if (context && context.state !== "closed") void context.close().catch(() => undefined);
    };
  }, [attachmentId]);

  return { decoded, peaks };
}

function AudioAttachmentPlayer({
  attachment,
  source,
}: {
  attachment: AttachmentReference;
  source: string;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const { decoded, peaks } = useAudioWaveform(attachment.id);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(attachment.duration ?? 0);
  const [loop, setLoop] = useState(false);
  const [volume, setVolume] = useState(1);
  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      try {
        await audio.play();
      } catch {
        setPlaying(false);
      }
    } else {
      audio.pause();
    }
  };

  return (
    <div className="audio-player">
      <audio
        onDurationChange={(event) => {
          if (Number.isFinite(event.currentTarget.duration))
            setDuration(event.currentTarget.duration);
        }}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        preload="metadata"
        ref={audioRef}
        src={source}
      />
      <div
        aria-label={`${decoded ? "Decoded" : "Preview"} waveform for ${attachment.title}`}
        className="audio-waveform"
        role="img"
      >
        <svg preserveAspectRatio="none" viewBox="0 0 100 32">
          {peaks.map((peak, index) => {
            const height = Math.max(1.5, peak * 28);
            return (
              <rect
                height={height}
                key={index}
                width={0.72}
                x={(index / peaks.length) * 100}
                y={(32 - height) / 2}
              />
            );
          })}
          <line
            className="audio-progress-line"
            x1={progress * 100}
            x2={progress * 100}
            y1="0"
            y2="32"
          />
        </svg>
      </div>
      <div className="audio-transport">
        <button
          aria-label={`${playing ? "Pause" : "Play"} ${attachment.title}`}
          onClick={() => void togglePlayback()}
          type="button"
        >
          {playing ? "Pause" : "Play"}
        </button>
        <span>{formatDuration(currentTime) ?? "0:00"}</span>
        <input
          aria-label={`Seek ${attachment.title}`}
          disabled={!duration}
          max={duration || 1}
          min="0"
          onChange={(event) => {
            const nextTime = Number(event.target.value);
            if (audioRef.current) audioRef.current.currentTime = nextTime;
            setCurrentTime(nextTime);
          }}
          step="0.01"
          type="range"
          value={Math.min(currentTime, duration || 1)}
        />
        <span>{formatDuration(duration) ?? "0:00"}</span>
      </div>
      <div className="audio-options">
        <label>
          <input
            checked={loop}
            onChange={(event) => {
              setLoop(event.target.checked);
              if (audioRef.current) audioRef.current.loop = event.target.checked;
            }}
            type="checkbox"
          />
          Loop
        </label>
        <label>
          <span>Volume</span>
          <input
            aria-label={`Volume for ${attachment.title}`}
            max="1"
            min="0"
            onChange={(event) => {
              const nextVolume = Number(event.target.value);
              setVolume(nextVolume);
              if (audioRef.current) audioRef.current.volume = nextVolume;
            }}
            step="0.05"
            type="range"
            value={volume}
          />
        </label>
      </div>
    </div>
  );
}

function AttachmentEditor({
  attachment,
  onView,
  source,
}: {
  attachment: AttachmentReference;
  onView?: () => void;
  source?: string;
}) {
  const removeAttachment = useEditorStore((state) => state.removeAttachment);
  const updateAttachment = useEditorStore((state) => state.updateAttachment);
  const [title, setTitle] = useState(attachment.title);
  const [description, setDescription] = useState(attachment.description);
  const [tags, setTags] = useState(attachment.tags.join(", "));
  const nextTags = tags
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
  const changed =
    title !== attachment.title ||
    description !== attachment.description ||
    nextTags.join("|") !== attachment.tags.join("|");

  return (
    <article className={`attachment-card is-${attachment.kind}`}>
      {attachment.kind === "photo" ? (
        source ? (
          <button
            aria-label={`View ${attachment.title}`}
            className="photo-preview-button"
            onClick={onView}
            type="button"
          >
            <img alt={attachment.description || attachment.title} src={source} />
            <span>Open viewer</span>
          </button>
        ) : (
          <div className="attachment-missing">Image data unavailable</div>
        )
      ) : source ? (
        <AudioAttachmentPlayer attachment={attachment} source={source} />
      ) : (
        <div className="attachment-missing">Audio data unavailable</div>
      )}
      <div className="attachment-editor-fields">
        <div className="attachment-meta">
          <span>{attachment.featured ? "Featured" : attachment.kind}</span>
          <small>
            {formatBytes(attachment.size)}
            {formatDuration(attachment.duration) ? ` · ${formatDuration(attachment.duration)}` : ""}
          </small>
        </div>
        <label>
          <span>{attachment.kind === "photo" ? "Title" : "Sample title"}</span>
          <input onChange={(event) => setTitle(event.target.value)} value={title} />
        </label>
        <label>
          <span>{attachment.kind === "photo" ? "Caption" : "Description"}</span>
          <textarea
            onChange={(event) => setDescription(event.target.value)}
            rows={3}
            value={description}
          />
        </label>
        <label>
          <span>
            Tags <small>comma separated</small>
          </span>
          <input onChange={(event) => setTags(event.target.value)} value={tags} />
        </label>
        <label className="attachment-featured">
          <input
            checked={attachment.featured}
            onChange={(event) =>
              updateAttachment(attachment.id, { featured: event.target.checked })
            }
            type="checkbox"
          />
          Featured {attachment.kind}
        </label>
        <div className="attachment-actions">
          <button
            className="is-danger"
            onClick={() => removeAttachment(attachment.id)}
            type="button"
          >
            <Icon name="trash" size={14} /> Remove
          </button>
          <button
            disabled={!changed}
            onClick={() =>
              updateAttachment(attachment.id, {
                title:
                  title || (attachment.kind === "photo" ? "Untitled photo" : "Untitled sample"),
                description,
                tags: nextTags,
              })
            }
            type="button"
          >
            Save details
          </button>
        </div>
      </div>
    </article>
  );
}

export function AttachmentsInspector({
  document,
  kind,
}: {
  document: PatchDocument;
  kind: "photo" | "audio";
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const addAttachment = useEditorStore((state) => state.addAttachment);
  const attachments = useMemo(
    () => document.attachments.filter((attachment) => attachment.kind === kind),
    [document.attachments, kind],
  );
  const urls = useAttachmentUrls(attachments);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const plural = kind === "photo" ? "photos" : "audio samples";
  const accepted =
    kind === "photo" ? "image/jpeg,image/png,image/webp" : "audio/ogg,audio/webm,audio/mpeg,.opus";

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setMessage(null);
    try {
      for (const file of [...files]) {
        const expectedPrefix = kind === "photo" ? "image/" : "audio/";
        const extensionIsOpus = kind === "audio" && file.name.toLowerCase().endsWith(".opus");
        if (!file.type.startsWith(expectedPrefix) && !extensionIsOpus) {
          throw new Error(`${file.name} is not a supported ${kind} file.`);
        }
        const limit = kind === "photo" ? PHOTO_LIMIT : AUDIO_LIMIT;
        if (file.size > limit) {
          throw new Error(`${file.name} exceeds the ${formatBytes(limit)} per-file limit.`);
        }
        await addAttachment(
          kind,
          file,
          kind === "audio" ? await attachmentDuration(file) : undefined,
        );
      }
      setMessage(`${files.length} ${files.length === 1 ? kind : plural} added locally.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="attachments-inspector">
      <section className="attachment-intro">
        <div>
          <span className="object-type">Local attachment</span>
          <h3>{kind === "photo" ? "Reference photos" : "Audio examples"}</h3>
          <p>
            {kind === "photo"
              ? "Keep physical wiring, rear-panel, and performance setup references with this patch."
              : "Keep a playable example beside the settings that produced it."}
          </p>
        </div>
        <input
          accept={accepted}
          aria-label={kind === "photo" ? "Add photos" : "Add audio samples"}
          multiple
          onChange={(event) => void addFiles(event.target.files)}
          ref={inputRef}
          type="file"
        />
      </section>
      <div className="attachment-summary">
        <span>
          {attachments.length} {attachments.length === 1 ? kind : plural}
        </span>
        <small>
          {formatBytes(attachments.reduce((total, attachment) => total + attachment.size, 0))} local
        </small>
      </div>
      {message && (
        <p aria-live="polite" className="attachment-message">
          {message}
        </p>
      )}
      {attachments.length ? (
        <div className="attachment-list">
          {attachments.map((attachment) => (
            <AttachmentEditor
              attachment={attachment}
              key={`${attachment.id}:${attachment.title}:${attachment.description}:${attachment.tags.join("|")}`}
              onView={
                attachment.kind === "photo" ? () => setSelectedPhotoId(attachment.id) : undefined
              }
              source={urls[attachment.id]}
            />
          ))}
        </div>
      ) : (
        <section className="inspector-card empty-tab">
          <span>{kind}</span>
          <h3>No {plural} added yet</h3>
          <p>
            The files stay on this device and are included when you export the patch or library.
          </p>
          <button disabled={busy} onClick={() => inputRef.current?.click()} type="button">
            <Icon name="plus" size={15} />{" "}
            {busy ? "Adding…" : `Add ${kind === "photo" ? "photo" : "audio"}`}
          </button>
        </section>
      )}
      {kind === "photo" && selectedPhotoId && urls[selectedPhotoId] && (
        <PhotoViewer
          attachments={attachments}
          onClose={() => setSelectedPhotoId(null)}
          onSelect={setSelectedPhotoId}
          selectedId={selectedPhotoId}
          urls={urls}
        />
      )}
    </div>
  );
}
